import React, { useEffect } from 'react';
import { graphql, navigate } from 'gatsby';
import PlanCatalog from '../components/PlanCatalog/PlanCatalog';
import PropTypes from 'prop-types';
import { getUrlParams } from '@lux/helpers';
import { connect } from 'react-redux';

import CisError from '../components/CisError';
import SEO from '../components/SEO';

import { userActions, CISCustomerLoginType } from '@detox/actions';
import notifications from '../config/notifications.js';
import { useLocation } from '@reach/router';
import { getUIAMEnabledWithToken } from '../selectors';
import { SkeletonGrid } from '../components/SkeletonLoader';
import { useDevice, Grid, Row } from '@dls/web';
import { useABTesting } from '@wec-core/ab-testing';
import { AbFeatureNames } from '../constants/ab-feature-names';

export const CisPlansPage = ({
  location,
  data,
  validateCISToken,
  getUserInformation,
  cisError,
  cisLoading,
  cisPlanRates,
  cisUserInfo,
  appState
}) => {
  const apigeeTokenEnabled = getUIAMEnabledWithToken(appState);
  const { isMobile } = useDevice();
  // AB Testing for ESHOP_VALIDATE_CIS
  const {
    isFeatureAllowed: isValidateCisEnabled,
    isPending: abTestApiPending
  } = useABTesting({
    featureName: AbFeatureNames.ESHOP_VALIDATE_CIS
  });

  // Check if token exists
  useEffect(() => {
    const urlParams = getUrlParams();
    const hasCisToken = Boolean(urlParams.sessionToken);
    const cisSessionType: CISCustomerLoginType =
      urlParams.source === 'myinfo' ? 'MY_INFO' : 'EMAIL';
    const hasCisData = Boolean(cisPlanRates);

    if (!hasCisToken) {
      navigate('/plans');
      return;
    }

    if (!abTestApiPending && !hasCisData && apigeeTokenEnabled) {
      getUserInformation().then(() => {
        if (urlParams.sessionToken !== cisUserInfo?.sessionToken) {
          validateCISToken(
            urlParams.sessionToken,
            cisSessionType,
            isValidateCisEnabled
          );
        }
      });
    }
  }, [
    cisPlanRates,
    validateCISToken,
    apigeeTokenEnabled,
    isValidateCisEnabled,
    abTestApiPending
  ]);

  let renderContent = (
    <>
      <PlanCatalog data={data} location={location} isCisPage={true} />
    </>
  );

  // if it is invalid, show cis error
  if (cisError && notifications[cisError]) {
    renderContent = (
      <CisError
        title={notifications[cisError].title}
        subTitle={notifications[cisError].subTitle}
        text={notifications[cisError].text}
        ctaText={notifications[cisError].ctaText}
        ctaUrl={notifications[cisError].ctaUrl}
      />
    );
  }
  return (
    <>
      <SEO
        title="CIS Plans"
        description="No Queuing. Beat the queues and shop from the comfort of your own home. Flexible Collection &amp; Delivery."
        index
      />
      {cisLoading ? (
        <Grid>
          <Row>
            <SkeletonGrid
              numberOfRow={3}
              numberOfColumn={isMobile ? 1 : 3}
              itemType="Card"
            />
          </Row>
        </Grid>
      ) : (
        renderContent
      )}
    </>
  );
};

CisPlansPage.defaultProps = {
  location: {}
};

CisPlansPage.propTypes = {
  data: PropTypes.object,
  validateCISToken: PropTypes.func,
  cisError: PropTypes.string,
  cisPlanRates: PropTypes.arrayOf(
    PropTypes.shape({
      planID: PropTypes.string,
      discountedPrice: PropTypes.string
    })
  ),
  appState: PropTypes.any
};

/* istanbul ignore next */
const mapStateToProps = state => {
  const { user } = state;
  return {
    cisLoading: (user.cis && user.cis.loading) || user.loading,
    cisError: user.cis && user.cis.error,
    cisUserInfo: user.cis?.information,
    cisPlanRates: user.cis.information && user.cis.information.rates,
    appState: state
  };
};

/* istanbul ignore next */
const mapDispatchToProps = {
  validateCISToken: userActions.validateCISToken,
  getUserInformation: userActions.getUserInformation
};

/* istanbul ignore next */
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(props => <CisPlansPage {...props} location={useLocation()} />);

export const query = graphql`
  {
    allPhone {
      edges {
        node {
          ...CatalogPhoneDetailFragment
        }
      }
    }
    allPlan(filter: { enabledOnPlanCatalog: { eq: true } }) {
      edges {
        node {
          ...PlanDetailFragment
        }
      }
    }
    allPlanGroup(
      filter: { enabledOnPlanCatalog: { eq: true }, segment: { in: "cis" } }
    ) {
      edges {
        node {
          groupName
          groupDisplayName
          enabledOnPlanCatalog
          shortDescription
          footerLiner
          enableIppForCIS
        }
      }
    }
    allCis {
      nodes {
        companyCode
        companyName
        desktopIconImage {
          src
        }
        mobileIconImage {
          src
        }
      }
    }
    allAemPages {
      edges {
        node {
          components {
            ...AemComponentsFragment
          }
          pageUrl
          title
        }
      }
    }

    allVariableConfig {
      edges {
        node {
          name
          value
        }
      }
    }
    allPhoneBanner {
      edges {
        node {
          id
          flowType
          enablePromoBanner
          enableCTA
          ctaText
          ctaLink
          description
          desktopFileReference
          mobileFileReference
          sectionDescription
          sectionTitle
          theme
          title
        }
      }
    }
  }
`;
